import { makeAutoObservable } from 'mobx'
import { RootStore } from '../../app/store'
import { UserPublicFlag } from '../auth/types'
import { captureException } from '@sentry/react'
import { getSeenFlag } from './config/newFeatures'
import { getFeaturesForUITarget, getFeatureForDismissalTarget } from './utils'

export class NoveltyFeedbackStore {
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this, {
      rootStore: false,
    })
  }

  private get user() {
    return this.rootStore.auth?.user
  }

  private get userPublicFlags(): string[] {
    return this.user?.publicFlags || []
  }

  private get userCreatedAt(): Date | null {
    return this.user?.createdAt ? new Date(this.user.createdAt) : null
  }

  isNew(uiTargetKey: string): boolean {
    const currentUser = this.user
    const createdAt = this.userCreatedAt

    if (!currentUser || !createdAt || !uiTargetKey) {
      return false
    }

    const relevantFeatures = getFeaturesForUITarget(uiTargetKey)
    const currentFlags = new Set(this.userPublicFlags)

    for (const feature of relevantFeatures) {
      const featureIntroducedDate = new Date(feature.introducedDate)
      const seenFlag = getSeenFlag(feature.id)

      const createdBeforeFeature = createdAt < featureIntroducedDate
      const notSeenYet = !currentFlags.has(seenFlag)

      if (createdBeforeFeature && notSeenYet) {
        return true
      }
    }

    return false
  }

  async markAsSeen(uiTargetKey: string): Promise<void> {
    const currentUser = this.user
    const createdAt = this.userCreatedAt

    if (!currentUser || !createdAt || !uiTargetKey) {
      return
    }

    const featureToDismiss = getFeatureForDismissalTarget(uiTargetKey)

    if (!featureToDismiss) {
      return
    }

    const featureId = featureToDismiss.id
    const seenFlag = getSeenFlag(featureId)
    const currentFlags = new Set(this.userPublicFlags)

    if (currentFlags.has(seenFlag)) {
      return
    }

    const featureIntroducedDate = new Date(featureToDismiss.introducedDate)
    const createdBeforeFeature = createdAt < featureIntroducedDate

    if (createdBeforeFeature) {
      try {
        await this.rootStore.auth.addUserPublicFlag(seenFlag as UserPublicFlag)
      } catch (error) {
        captureException(error)
      }
    }
  }
}
